import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    MapPin, Phone, Loader, Home as HomeIcon, Key, Wifi as WifiIcon,
    ChevronDown, AlertCircle, BookText
} from 'lucide-react';
import { HouseManual, ManualItem } from '../types';
import { getManualForGuest } from '../services/apiService';
import { parseBlocks } from '../components/ManualBlockEditor';

const GuestManualView = () => {
    const { guestSlug } = useParams<{ guestSlug: string }>();
    const [manual, setManual] = useState<HouseManual | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [openItem, setOpenItem] = useState<number | null>(null);

    useEffect(() => {
        const load = async () => {
            if (!guestSlug) return;
            try {
                const data = await getManualForGuest(guestSlug);
                if (data) setManual(data);
                else setNotFound(true);
            } catch {
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [guestSlug]);

    if (loading) {
        return (
            <>
                <NoIndex/>
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <Loader className="w-8 h-8 animate-spin text-brand"/>
                </div>
            </>
        );
    }

    if (notFound || !manual) {
        return (
            <>
                <NoIndex/>
                <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
                    <div className="text-center max-w-sm">
                        <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4"/>
                        <h1 className="font-serif font-bold text-2xl text-gray-800 mb-2">Manual not found</h1>
                        <p className="text-sm text-gray-500">The link you used is invalid or has been revoked.</p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Helmet>
                <meta name="robots" content="noindex, nofollow"/>
                <title>{manual.houseName} — House Manual</title>
            </Helmet>

            <div className="min-h-screen bg-[#F6F4F1] font-sans pb-16">
                {/* HEADER */}
                <header className="bg-white border-b border-gray-100">
                    <div className="max-w-2xl mx-auto px-4 md:px-6 py-8 text-center">
                        <a
                            href="https://anotherhome.us"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block"
                        >
                            <img
                                src="/manual1.png"
                                alt="Another Home"
                                className="h-20 md:h-24 w-auto mx-auto"
                            />
                        </a>
                        <p className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-primary">
                            Hospitality Services
                        </p>
                        <a
                            href="https://anotherhome.us"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block mt-1 text-xs font-bold uppercase tracking-widest text-brand hover:underline mb-6"
                        >
                            anotherhome.us
                        </a>
                        <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-brand bg-brand/10 px-3 py-1 rounded-full mb-4">
                            <BookText className="w-3 h-3"/> House Manual
                        </div>
                        <h1 className="font-serif font-bold text-3xl md:text-4xl text-primary leading-tight">
                            {manual.houseName}
                        </h1>
                        {manual.address && (
                            <p className="mt-3 text-sm text-gray-500 flex items-center justify-center gap-2">
                                <MapPin className="w-4 h-4"/> {manual.address}
                            </p>
                        )}
                        {manual.badge && (
                            <span className="mt-3 inline-block text-[10px] font-bold uppercase tracking-widest text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                                {manual.badge}
                            </span>
                        )}
                    </div>
                </header>

                <main className="max-w-2xl mx-auto px-4 md:px-6 py-8 space-y-6">

                    {/* WELCOME */}
                    {(manual.welcomeMessage || manual.assistancePhone) && (
                        <Card>
                            {manual.welcomeMessage && (
                                <p className="text-gray-700 leading-relaxed text-[15px]">{manual.welcomeMessage}</p>
                            )}
                            {manual.assistancePhone && (
                                <a
                                    href={`tel:${manual.assistancePhone.replace(/\s+/g, '')}`}
                                    className="mt-5 flex items-center justify-center gap-3 bg-brand text-white font-bold py-4 rounded-xl hover:bg-[#c42e40] transition-colors shadow-md"
                                >
                                    <Phone className="w-5 h-5"/> Call assistance — {manual.assistancePhone}
                                </a>
                            )}
                        </Card>
                    )}

                    {/* CHECK-IN */}
                    {(manual.checkinVideoUrl || manual.intercomCode || manual.keyBoxCode) && (
                        <SectionTitle icon={<HomeIcon className="w-5 h-5"/>} title="Check-in"/>
                    )}
                    {manual.checkinVideoUrl && (
                        <Card noPad>
                            <video
                                src={manual.checkinVideoUrl}
                                controls
                                playsInline
                                preload="metadata"
                                className="w-full max-h-[60vh] bg-black"
                            />
                        </Card>
                    )}
                    {(manual.intercomCode || manual.keyBoxCode) && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {manual.intercomCode && (
                                <CodeCard icon={<Key className="w-5 h-5"/>} label="Intercom Code" value={manual.intercomCode}/>
                            )}
                            {manual.keyBoxCode && (
                                <CodeCard icon={<Key className="w-5 h-5"/>} label="Key Box Code" value={manual.keyBoxCode}/>
                            )}
                        </div>
                    )}

                    {/* WIFI */}
                    {(manual.wifiName || manual.wifiPassword) && (
                        <>
                            <SectionTitle icon={<WifiIcon className="w-5 h-5"/>} title="Wi-Fi"/>
                            <Card>
                                {manual.wifiName && (
                                    <div className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                                        <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Network</span>
                                        <span className="font-bold text-gray-800">{manual.wifiName}</span>
                                    </div>
                                )}
                                {manual.wifiPassword && (
                                    <div className="flex justify-between items-center py-2">
                                        <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Password</span>
                                        <span className="font-mono font-bold text-gray-800 bg-gray-50 px-3 py-1 rounded-lg">{manual.wifiPassword}</span>
                                    </div>
                                )}
                            </Card>
                        </>
                    )}

                    {/* ITEMS */}
                    {manual.items && manual.items.length > 0 && (
                        <>
                            <SectionTitle icon={<BookText className="w-5 h-5"/>} title="Instructions & Amenities"/>
                            <div className="space-y-3">
                                {manual.items.map((item, idx) => (
                                    <ItemAccordion
                                        key={idx}
                                        item={item}
                                        open={openItem === idx}
                                        onToggle={() => setOpenItem(openItem === idx ? null : idx)}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </main>

                <footer className="text-center text-xs text-gray-400 mt-12 px-4">
                    © {new Date().getFullYear()} Another Home — House Manual
                </footer>
            </div>
        </>
    );
};

// ============================================================
// Components
// ============================================================

const NoIndex: React.FC = () => (
    <Helmet>
        <meta name="robots" content="noindex, nofollow"/>
    </Helmet>
);

const Card: React.FC<{ children: React.ReactNode; noPad?: boolean }> = ({ children, noPad }) => (
    <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden ${noPad ? '' : 'p-6'}`}>
        {children}
    </div>
);

const SectionTitle: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
    <h2 className="font-serif font-bold text-xl text-primary flex items-center gap-3 pt-4">
        <span className="text-brand">{icon}</span> {title}
    </h2>
);

const CodeCard: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 text-center">
        <div className="flex items-center justify-center gap-2 text-brand mb-2">
            {icon}
            <span className="text-[10px] uppercase tracking-widest font-bold text-gray-500">{label}</span>
        </div>
        <p className="font-mono font-bold text-3xl tracking-[0.3em] text-primary">{value}</p>
    </div>
);

const ItemAccordion: React.FC<{ item: ManualItem; open: boolean; onToggle: () => void }> = ({ item, open, onToggle }) => {
    const blocks = parseBlocks(item.content);
    const hasLegacy = !blocks.length && (item.instructions || item.videoUrl);
    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
            >
                <span className="font-bold text-gray-800">{item.title || 'Untitled'}</span>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}/>
            </button>
            {open && (
                <div className="border-t border-gray-100 p-5 space-y-4">
                    {blocks.map(block => {
                        if (block.type === 'heading') {
                            return <h3 key={block.id} className="font-serif font-bold text-xl text-primary">{block.text}</h3>;
                        }
                        if (block.type === 'paragraph') {
                            return <p key={block.id} className="text-gray-700 text-[15px] leading-relaxed whitespace-pre-wrap">{block.text}</p>;
                        }
                        if (block.type === 'image' && block.url) {
                            return (
                                <figure key={block.id} className="space-y-2">
                                    <img src={block.url} alt={block.text || ''} className="w-full rounded-xl bg-gray-50"/>
                                    {block.text && <figcaption className="text-xs text-gray-400 text-center italic">{block.text}</figcaption>}
                                </figure>
                            );
                        }
                        if (block.type === 'video' && block.url) {
                            return (
                                <div key={block.id} className="rounded-xl overflow-hidden bg-black">
                                    <video src={block.url} controls playsInline preload="metadata" className="w-full max-h-[50vh] object-contain"/>
                                </div>
                            );
                        }
                        return null;
                    })}

                    {hasLegacy && item.videoUrl && (
                        <div className="rounded-xl overflow-hidden bg-black">
                            <video src={item.videoUrl} controls playsInline preload="metadata" className="w-full max-h-[50vh] object-contain"/>
                        </div>
                    )}
                    {hasLegacy && item.instructions && (
                        <div className="text-gray-700 text-[15px] leading-relaxed whitespace-pre-wrap">
                            {item.instructions}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default GuestManualView;
